<template lang="pug">
content-page
	div(slot="nav")
		navigation-default
	h1 {{contentpage.name}}
	.infosheet
		ad.mb-6(location="sidebar")
		v-list(v-if="airport.airlines" subheading dense)
			template(v-for="airline in airport.airlines")
				a(:href="airline.url" target="_blank" rel="nofollow")
					v-divider
					v-list-item.pl-2.pr-0
						v-list-item-content
							v-list-item-title(style="font-size:15px")
								span.font-weight-bold.primary--text {{airline.airline.name}}
								|
								| {{$t('page.areas._area.reachability:price_from')}}
								|
								span.orange--text.font-weight-bold €{{airline.price}},-
						v-list-item-action.ma-0
							v-btn.body-2(color="warning" small depressed :href="airline.url" target="_blank" rel="nofollow") {{$t('page.areas._area.reachability:check_button')}}
	ws-content(:value="contentpage.content")
		template(slot="airport-summary" v-if="$i18n.locale !== 'de-DE'")
			ws-content(:value="airport.content")
		template(slot="area-table")
			v-data-table(:items="airport.areas" hide-default-footer sort-by="minutes" :mobile-breakpoint="0" disable-pagination :headers="[{text: $t('page.airports._airport:table.heading.areas.around_{airport}', {airport: airport.name}), sortable:false}, {text: $t('page.airports._airport:table.heading.time.driving'), sortable:false, align: 'right'}]")
				template(#item="{item}")
					tr
						td
							nuxt-link(:to="localePath({ name: 'areas-area', params: { area: item.area.slug }})") {{ item.area.name }}
						td.text-right.text-no-wrap
							template(v-if="item.distance") {{$t('page.areas._area.reachability:{time}_to_drive', {time: formatMinutes(item.minutes)})}}

		template(slot="area-map")
			client-only
				mapbox.mt-6(
					ref="map"
					style="height:600px"
					:layers="layers"
					:sources="sources"
					:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
					@map-styleload="mapStyle")
					div(slot="popup")
						span.h3(v-if="popupData.__typename == 'Airport'") {{popupData.name}}
						span.h3(v-if="popupData.__typename == 'Area'") {{popupData.name}} ({{$t('page.areas._area.reachability:{time}_to_drive', {time: formatMinutes(popupData.minutes)})}})

</template>

<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import { ref } from '@vue/composition-api';
import NavigationDefault from '@/components/navigation-default';
import areasReachability from '@/mixins/areas-reachability.js';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import { interpolateFields } from '@/utils/interpolate.js';
import useMap from '@/use/map';
import AirlinePrices from '@/components/airports/airline-prices';
import { gluelist } from '@/utils/gluelist';

const getAirport = gql`
	query getAirport($slug: String!, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		airport(slug: $slug) {
			id
			slug
			name
			content {
				markdown
			}
			geography {
				location {
					type
					coordinates
				}
			}
			areas {
				distance
				minutes
				area {
					id
					slug
					name
					geography {
						location {
							type
							coordinates
						}
					}
				}
			}
			distance {
				minutes
			}
			id
			name
			slug
			flights
			airlines {
				id
				url
				price
				airline {
					id
					name
				}
			}
			geography {
				location {
					type
					coordinates
				}
			}
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/vliegvelden/:airport',
			'de-DE': '/flughaefen/:airport',
			'nl-BE': '/vliegvelden/:airport'
		}
	},
	name: 'AirportsAirport',
	components: {
		AirlinePrices,
		NavigationDefault,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},

	mixins: [areasReachability],
	layout: 'default',
	setup() {
		const mapItems = ref([]);
		const { markers, sources, attachListeners, popupData } = useMap(mapItems);

		const layers = [
			{
				id: 'markers',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			mapItems,
			sources,
			layers,
			markers,
			attachListeners,
			popupData
		};
	},
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: getAirport,
				variables: {
					slug: params.airport,
					path: route.path,
					fallback: route.path.replace(params.airport, '_airport')
				},
				errorPolicy: 'all',
				fetchPolicy: 'network-only'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'airports-airport-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				const interpolater = interpolateFields({
					find: /{(airport|areas)}/gi,
					replace: (match, group) => {
						return {
							airport: data.airport.name,
							areas: gluelist(
								data.airport.areas.slice(0, 5).map(area => area.area.name),
								', ',
								app.i18n.t('global.and')
							)
						}[group];
					}
				});
				interpolater(data.contentpage.meta, ['title', 'description']);
				interpolater(data.contentpage.content, ['markdown']);
				interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({ sidebar: { visible: true } });
				app.breadcrumbs({
					[app.i18n.t('page.airports:breadcrumb')]: {
						name: 'airports'
					},
					[data.airport.name]: {
						name: 'airports-airport',
						params: { airport: data.airport.slug }
					}
				});

				return data;
			})
			.catch(app.$defaultCatcher)
			.catch(error => {
				return app.httpError(500, null, 'airports-airport-02', error);
			});
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	mounted() {
		this.mapItems = this.airport.areas.map(area => {
			const newArea = this.$lodash.cloneDeep(area.area);
			newArea.distance = area.distance;
			newArea.minutes = area.minutes;
			newArea.icon = 'resort-marker';
			return newArea;
		});

		this.mapItems.push(this.airport);

		if (this.$refs.map) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}
	},
	methods: {
		mouseClick(feature) {
			if (feature.properties.icon !== 'airport-marker') {
				this.$router.push(
					this.localePath({
						name: 'areas-area',
						params: { area: feature.properties.slug }
					})
				);
			}
		},
		mapStyle(map) {
			this.$refs.map.fitToCurrentBounds();
			this.attachListeners(
				map,
				this.$refs.map,
				'markers',
				'geojson-markers',
				this.mouseClick
			);
		}
	}
};
</script>
